import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService } from '../base/base.service';
import { TinyMCE } from 'tinymce';
import {
  CreateVariableResponseType,
  SortVariableRequest,
  SortVariableResponse,
  WebApiResponse,
} from '../../models/types';
import { DocumentContent } from '../../models/types/document.types';
import { DocumentQueryHelper, GenericQueryOptions, GetVariablesGloballyOptions } from './helpers/document-helper';
import { CategoryHelper, CategoryType } from './helpers/category-helper';
import { Observable } from 'rxjs';

declare let tinymce: TinyMCE;

@Injectable({
  providedIn: 'root',
})
export class DocumentService extends BaseService {
  private readonly htmlEntityRegex = {
    amp: /&amp;/gm,
    gt: /&gt;/gm,
    lt: /&lt;/gm,
  };

  constructor(
    httpClient: HttpClient,
    private readonly documentQueryHelper: DocumentQueryHelper,
    private readonly categoryHelper: CategoryHelper
  ) {
    super(httpClient);
  }

  // Editor content management methods
  getEditorContent(): string {
    return (
      tinymce.activeEditor?.getContent({
        format: 'html',
        contextual: true,
      }) ?? ''
    );
  }

  sanitizeHtmlContent(content: string): string {
    return content
      .replace(this.htmlEntityRegex.amp, '&')
      .replace(this.htmlEntityRegex.gt, '>')
      .replace(this.htmlEntityRegex.lt, '<');
  }

  prepareHtmlContent(currentContent: DocumentContent, newContent: string): DocumentContent {
    const sanitizedContent = this.sanitizeHtmlContent(newContent);

    return {
      ...currentContent,
      body: sanitizedContent,
    };
  }

  // API methods
  createVariable(request: any, documentId: string): WebApiResponse<CreateVariableResponseType> {
    const URI = `document/${documentId}/variable`;

    return this.put(URI, request);
  }

  createField(request: any): WebApiResponse<any> {
    const URI = 'variables';

    return this.post(URI, request);
  }

  createFieldByGlobal(documentId: string, request: any): WebApiResponse<any> {
    const URI = `documents/${documentId}/variables/global/copy`;

    return this.put(URI, request);
  }

  assignVariableToDocument(request: any): WebApiResponse<any> {
    const URI = `documents/${request.document_id}/variables`;

    return this.post(URI, request);
  }

  assignExistingVariableToDocument(documentId: string, variableId: string, request: any): WebApiResponse<any> {
    const URI = `documents/${documentId}/variables/${variableId}`;

    return this.put(URI, request);
  }

  getVariablesGlobally(options?: GetVariablesGloballyOptions): WebApiResponse<any> {
    const URI = 'variables';
    const queryString = this.documentQueryHelper.buildCompleteQueryString(options);

    return this.get(`${URI}${queryString}`);
  }

  getAllVariables(automation: string): Observable<any> {
    const URI =
      'variables?page=1&limit=1000&filters={"automation":{"=":"' +
      automation +
      '"}, "isAConditional":{"=":false}}&sort={"position":"asc"}';

    return this.get(URI);
  }

  getCategoriesGlobally(options?: GetVariablesGloballyOptions): WebApiResponse<any> {
    const URI = 'categories';
    const queryString = this.documentQueryHelper.buildCompleteQueryString(options);

    return this.get(`${URI}${queryString}`);
  }

  createCategoryOfDocument(
    name: string,
    owner: string,
    formId: string,
    description = '',
    type: CategoryType = 'CUSTOM'
  ): WebApiResponse<any> {
    const URI = `forms/${formId}/categories`;
    const payload = this.categoryHelper.buildPayload(name, owner, formId, description, type);

    return this.post(URI, payload);
  }

  updateVariable(request: any, documentId: string, variableId: string): WebApiResponse<any> {
    const URI = `document/${documentId}/variable/${variableId}`;

    return this.put(URI, request);
  }

  editVariable(documentId: string, categoryId: string, variableId: string, request: any): WebApiResponse<any> {
    const URI = `documents/${documentId}/categories/${categoryId}/variables/${variableId}`;

    return this.put(URI, request);
  }

  editVariableDocument(categoryId: string, variableId: string, request: any): WebApiResponse<any> {
    const URI = `documents/categories/${categoryId}/variables/${variableId}`;

    return this.put(URI, request);
  }

  updateConditional(request: any, documentId: string, conditionalId: string): WebApiResponse<any> {
    const URI = `documents/${documentId}/conditionals/${conditionalId}/options/mutations`;

    return this.put(URI, request);
  }

  cloneVariable(documentId: string, categoryId: string, variableId: string, partial: string): WebApiResponse<any> {
    const URI = `documents/${documentId}/categories/${categoryId}/variables/${variableId}/partials/${partial}`;

    return this.put(URI, {});
  }

  sortVariablePosition(request: SortVariableRequest, variableId: string): WebApiResponse<SortVariableResponse> {
    const URI = `documents/variable/${variableId}/move`;

    return this.put(URI, request);
  }

  deleteVariable(documentId: string, categoryId: string, variableId: string): WebApiResponse<any> {
    const URI = `documents/${documentId}/categories/${categoryId}/variables/${variableId}`;

    return this.delete(URI);
  }

  createCategory(request: any): WebApiResponse<any> {
    const URI = 'category';

    return this.post(URI, request);
  }

  updateCategory(categoryId: string, request: any): WebApiResponse<any> {
    const URI = `category/${categoryId}`;

    return this.put(URI, request);
  }

  deleteCategory(categoryId: string): WebApiResponse<any> {
    const URI = `categories/${categoryId}`;

    return this.delete(URI);
  }

  createEmptyDocument(request: any): WebApiResponse<any> {
    const URI = 'document';

    return this.post(URI, request);
  }

  updateDocument(request: any, documentId: string): WebApiResponse<any> {
    const URI = `document/${documentId}`;

    return this.put(URI, request);
  }

  updateDocumentHtmlToWord(request: any, documentId: string): WebApiResponse<any> {
    const URI = `document/${documentId}/htmltowords3`;

    return this.post(URI, request);
  }

  getDocumentObs(
    userId: string,
    start: number,
    limit: number,
    sortBy?: string | string[],
    order?: string
  ): WebApiResponse<any> {
    const params = { user_id: userId };
    const URI = `document/user/query?start=${start}&limit=${limit}&sortBy=${sortBy}&order=${order}`;

    return this.post(URI, params);
  }

  getDocumentById(documentId: string, status?: string): WebApiResponse<any> {
    const URI = `document/${documentId}/status-doc/${status}`;

    return this.get(URI);
  }

  getDocumentByIdPublic(documentId: any): WebApiResponse<any> {
    const URI = `document/${documentId}/status-doc/COMPLETED/public`;

    return this.get(URI);
  }

  deleteDocument(documentId: string): WebApiResponse<any> {
    const URI = `document/${documentId}`;

    return this.delete(URI);
  }

  convertDocument(request: any): WebApiResponse<any> {
    const URI = 'document/wordtohtml';

    return this.post(URI, request);
  }

  cloneOrTransferDocument(documentId: string, newOwner?: string): WebApiResponse<any> {
    const URI = 'document/clone';
    const dataDocument = {
      idTemplate: documentId,
      newOwner,
    };

    return this.post(URI, dataDocument);
  }

  /**
   * Get document settings
   * @param documentId
   */
  getDocumentSettings(documentId: string): WebApiResponse<any> {
    const URI = `documents/${documentId}/settings`;

    return this.get(URI);
  }

  /**
   * Set document settings
   * @param documentId
   * @param payload
   */
  setDocumentSettings(documentId: string, payload: any): WebApiResponse<any> {
    const URI = `documents/${documentId}/settings`;

    return this.post(URI, payload);
  }

  /**
   * Get variables by criteria
   * @param filters JSON encoded filters
   * @param sort JSON encoded sort options
   * @param page Page number
   * @param limit Items per page
   */
  getVariablesByCriteria(filters?: any, sort?: string, page?: number, limit?: number): WebApiResponse<any> {
    let URI = 'variables/criteria?';

    if (filters) {
      URI += `filters=${JSON.stringify(filters)}`;
    }

    if (sort) {
      URI += `&sort=${sort}`;
    }

    if (page) {
      URI += `&page=${page}`;
    }

    if (limit) {
      URI += `&limit=${limit}`;
    }

    return this.get(URI);
  }

  /**
   * Get variable by ID
   * @param variableId ID of the variable to retrieve
   */
  getVariableById(variableId: string): WebApiResponse<any> {
    const URI = `variables/${variableId}`;

    return this.get(URI);
  }

  deleteVariableFromCategory(categoryId: string, variableId: string): WebApiResponse<any> {
    const URI = `documents/categories/${categoryId}/variables/${variableId}`;

    return this.delete(URI);
  }

  deleteVariablePartial(documentId: string, variableId: string, partialId: string): WebApiResponse<any> {
    const URI = `documents/${documentId}/variables/${variableId}/partials/${partialId}`;

    return this.delete(URI);
  }

  updateElementInDocument(request: any, categoryId: string, elementId: string): WebApiResponse<any> {
    const URI = `documents/categories/${categoryId}/variables/${elementId}`;

    return this.put(URI, request);
  }

  copyElementInDocument(request: any, documentId: string, elementId: string): WebApiResponse<any> {
    const URI = `documents/${documentId}/variables/${elementId}`;

    return this.put(URI, request);
  }
}
